<template>
  <v-container fill-height class="base-auth-form-container">
    <v-layout>
      <v-flex>
        <v-card class="base-auth mb-8" outlined>
          <v-app-bar dark color="primary">
            <v-btn
              v-if="currentActiveGroup > 0"
              icon
              @click="currentActiveGroup -= 1"
            >
              <v-icon>{{ icons.mdiArrowLeft }}</v-icon>
            </v-btn>
            <h3>{{ formTitle }}</h3>
            <v-spacer></v-spacer>
          </v-app-bar>
          <ValidationObserver ref="authForm" v-slot="{ invalid }">
            <v-form @submit.prevent="onSubmit">
              <v-card-text>
                <v-col>
                  <span
                    v-if="mappedFormAttributesGroups.length > 1"
                    class="primary--text mb-4 d-block"
                  >
                    {{ $t('registerScreen.stepText')}} {{currentActiveGroup + 1}}/{{mappedFormAttributesGroups.length }}
                  </span>
                  <template
                    v-for="(step, stepIndex) in mappedFormAttributesGroups"
                  >
                    <ValidationObserver
                      :key="stepIndex"
                      :ref="`validation_observer_${stepIndex}`"
                    >
                      <template
                        v-for="({attributes, rules, title, description}, groupIndex) in step"
                      >
                        <div
                          v-show="currentActiveGroup === stepIndex"
                          :key="groupIndex"
                          class="mb-3"
                        >
                          <h3>{{ $t(title) }}</h3>
                          <p v-if="description" class="description pre-line">{{ $t(description) }}</p>
                          <auth-form
                            :attributes="attributes"
                            :rules="rules"
                            :bool-has-defined-title="!!(title && attributes[0] && attributes[0].type === 'bool')"
                            :full-form-data="fullFormData"
                            @setRequestBody="setRequestBody"
                          />
                        </div>
                      </template>
                    </ValidationObserver>
                  </template>
                  <div
                    v-if="!signupMode"
                    class="text-caption required-hint"
                  >
                    {{ $t('registerScreen.requiredFieldAlertText')}}
                  </div>
                  <v-alert
                    v-if="error"
                    type="error"
                    dense
                    class="text-caption mt-3 mb-0"
                  >
                    {{ errorMessage }}
                  </v-alert>
                </v-col>
              </v-card-text>

              <div
                v-show="recaptchaEnabled"
                ref="register-recaptcha-container"
                class="g-recaptcha register-recaptcha-container"
                :data-sitekey="recaptchaSiteKey"
                data-callback="closeAlert"
              ></div>

              <v-alert id="recaptcha-alert" class="mx-7 vuetify-alert recaptcha-alert" dense type="warning">
                {{$t('messages.recaptchaMessage')}}
              </v-alert>

              <v-card-actions class="mr-5 mb-5">
                <v-spacer></v-spacer>
                <v-btn
                  v-show="mappedFormAttributesGroups.length - 1 > currentActiveGroup"
                  type="button"
                  color="primary"
                  @click="goToNextStep"
                >
                  {{ $t('registerScreen.nextButtonText') }}
                </v-btn>
                <v-btn
                  v-show="mappedFormAttributesGroups.length - 1 === currentActiveGroup"
                  type="submit"
                  color="primary"
                  :disabled="invalid || loading"
                  :loading="loading"
                >
                  {{ submitButtonLabel }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </ValidationObserver>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script src="https://www.google.com/recaptcha/api.js" async defer></script>
<script>
import _ from 'lodash'
import {mapGetters, mapActions} from 'vuex'
import {mdiArrowLeft} from '@mdi/js'
import {storage} from '@/store'
import AuthForm from '@/components/auth/AuthForm'
import cleanRequestBody from '@/utils/auth/cleanRequestBody'
import {loadRecaptchaScript, showAlert} from '@/firebase'

export default {
  components: {
    AuthForm,
  },
  props: {
    signupMode: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      currentActiveGroup: 0,
      error: false,
      errorMessage: '',
      icons: {mdiArrowLeft},
      loading: false,
      recaptchaResponse: '',
      requestBody: {
        custom_fields: []
      },
    }
  },
  computed: {
    ...mapGetters([
      'navigationSettings',
      'registrationAttributes',
      'verificationAttributes',
      'extensionSettings',
      'user'
    ]),
    fullFormData() {
      if (this.signupMode) {
        return this.requestBody
      }

      // Verification only shows missing fields, so runtime conditions
      // (e.g. state depends on country) must see the saved profile too.
      // Form input wins over profile values.
      return {
        ...this.requestBody,
        custom_fields: _.unionBy(
          this.requestBody.custom_fields,
          _.get(this.user, 'custom_fields', []),
          'key'
        )
      }
    },
    formTitle() {
      return this.signupMode
        ? this.$t('registerScreen.header')
        : this.$t('verificationScreen.heading')
    },
    recaptchaEnabled() {
      return this.signupMode && _.get(this.extensionSettings, 'firebase.recaptcha.enabled', false)
    },
    recaptchaSiteKey() {
      return _.get(this.extensionSettings, 'firebase.recaptcha.site_key', '')
    },
    submitButtonLabel() {
      return this.signupMode
        ? this.$t('registerScreen.registerButtonText')
        : this.$t('save')
    },
    registerNavigationComponents() {
      return _.get(
        this.navigationSettings,
        'screens.groupedRegisterScreen.components',
        []
      )
    },
    formAttributesGroups() {
      return this.signupMode
        ? this.registrationAttributes
        : this.verificationAttributes
    },
    mappedFormAttributesGroups() {
      return this.formAttributesGroups.reduce(
        (attributes, formAttrGroup) => {
          const stepHasSections = formAttrGroup.some((section) => {
            return section.attributes.length > 0
          })

          if (stepHasSections) {
            const mappedStep = formAttrGroup.map((section) => {
              const navigationGroup = this.getNavigationGroupByKey(
                section.key
              )
              const group = navigationGroup
                ? {...section, navigationGroup}
                : section

              return this.addAttributesRules(group)
            })

            const groupWithAttributes = mappedStep.filter((section) => {
              return section.attributes.length > 0
            })

            if (groupWithAttributes.length > 0) {
              attributes.push(groupWithAttributes)
            }
          }

          return attributes
        },
        []
      )
    },
  },
  mounted() {
    if (this.recaptchaEnabled) {
      loadRecaptchaScript()
    }
  },
  methods: {
    ...mapActions([
      'tosScreen',
      'signUp',
      'updateUserProfile',
    ]),
    getNavigationGroupByKey(componentKey) {
      const components = this.registerNavigationComponents

      if (_.isEmpty(components) || _.isEmpty(componentKey)) {
        return null
      }

      return components.find(
        (component) => {
          return _.get(component, 'key', '') === componentKey
        }
      ) || null
    },
    addAttributesRules(group) {
      let childrenAttributes = {}

      if (group.navigationGroup) {
        childrenAttributes = {
          rules: {
            ..._.get(group, 'navigationGroup.props.children', []),
          },
        }
      } else {
        childrenAttributes = {
          rules: {},
        }
      }

      childrenAttributes.attributes = _.get(group, 'attributes', []),
      childrenAttributes.key = _.get(group, 'key', '')
      childrenAttributes.title = _.get(group, 'title', '')
      childrenAttributes.description = _.get(group, 'description', '')

      return childrenAttributes
    },
    goToNextStep() {
      if (this.recaptchaEnabled) {
        this.recaptchaResponse = grecaptcha.getResponse();
        if (this.recaptchaResponse.length === 0) {
          showAlert(this.recaptchaEnabled);
          return;
        }
      }
      const currentValidationObserver = this.$refs[
        `validation_observer_${this.currentActiveGroup}`
      ]

      if (
        currentValidationObserver &&
        currentValidationObserver[0].flags.invalid
      ) {
        currentValidationObserver[0].validate()
        return
      }

      this.currentActiveGroup += 1
    },
    setRequestBody(body) {
      const bodyCopy = _.cloneDeep(body)
      bodyCopy.custom_fields.forEach((field) => {
        const indexInRequestBody =
          this.requestBody.custom_fields.findIndex(
            (body_custom_field) => {
              return field.key === body_custom_field.key
            }
          )
        if (indexInRequestBody >= 0) {
          this.requestBody.custom_fields[indexInRequestBody] = field
        } else {
          this.requestBody.custom_fields.push(field)
        }
      })
      delete bodyCopy.custom_fields
      this.requestBody = {...this.requestBody, ...bodyCopy}
    },
    onSubmit() {
      if (this.recaptchaEnabled) {
        this.recaptchaResponse = grecaptcha.getResponse();
        if (this.recaptchaResponse.length === 0) {
          showAlert(this.recaptchaEnabled);
          return;
        }
      }
      this.$refs.authForm.validate().then((result) => {
        if (result) {
          if (this.signupMode) {
            if (this.requestBody.terms_and_privacy) {
              this.onRegister()
            } else {
              this.tosScreen({
                show: true,
                acceptAction: this.onRegister,
                rejectAction: this.rejectTOS
              })
            }
          } else {
            this.onVerify()
          }
        }
      })
    },
    async onRegister() {
      this.loading = true
      const body = cleanRequestBody(this.requestBody)

      try {
        body.recaptchaResponse = this.recaptchaResponse
        await this.signUp(body)

        this.loading = false
        this.error = false
        this.tosScreen({show: false})
        if (this.$store.getters.isOpenIdProvider) {
          this.$router.push({name: 'openid'});
        } else {
          this.$router.push({name: 'home'})
        }
      } catch (error) {
        this.loading = false
        this.error = true
        this.tosScreen({show: false})

        if (
          _.has(error, 'code') &&
          error.code === 'USER_ALREADY_EXISTS'
        ) {
          this.errorMessage =
            this.$t('registerScreen.registerErrorUserAlreadyExists')
        } else {
          this.errorMessage = this.$t('registerScreen.registerError')
        }
      }
    },
    async onVerify() {
      this.addToStorage()
      this.loading = true
      const body = cleanRequestBody(this.requestBody)

      try {
        await this.updateUserProfile(body)

        this.loading = false
        this.error = false
        if (this.$store.getters.isOpenIdProvider) {
          this.$router.push({name: 'openid'});
        } else {
          this.$router.push({name: 'home'})
        }
      } catch (error) {
        this.loading = false
        this.error = true

        if (
          _.has(error, 'email') && !_.isEmpty(error, 'email')
        ) {
          this.errorMessage =
            this.$t('registerScreen.registerErrorUserAlreadyExists')
        } else {
          this.errorMessage =
            this.$t('registerScreen.verificationError')
        }
      }
    },
    addToStorage() {
      let showOnce = {
        userAttributes: [],
        customUserFields: []
      }

      const storageItems = JSON.parse(storage.getItem('askedUserFields'))
      if (storageItems) {
        showOnce = storageItems
      }

      this.verificationAttributes.forEach((groups) => {
        groups.forEach((group) => {
          group.attributes.forEach((attr) => {
            if (!attr.required) {
              if (attr.identity === 'custom_fields') {
                showOnce.customUserFields.push(attr.key)
              } else {
                showOnce.userAttributes.push(attr.key)
              }
            }
          })
        })
      })

      storage.setItem('askedUserFields', JSON.stringify(showOnce))
    },
    rejectTOS() {
      this.tosScreen({show: true})
    }
  },
}
</script>

<style lang="scss" scoped>
.base-auth {
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
  border-radius: 8px;
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.12);
}

.v-form::v-deep .row > .description {
  width: 100%;

  p {
    margin-bottom: 0 !important;
  }
}

.description {
  opacity: 0.8;
  padding: 16px 0;
}
.base-auth .register-recaptcha-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.base-auth-form-container {
  .recaptcha-alert {
    display: none;
  }
}
</style>
